'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';

// Configuration constants - tuned to prevent accidental triggers while scrolling up
const PULL_THRESHOLD = 100;        // Distance needed to trigger refresh (increased from 80)
const PULL_START_THRESHOLD = 30;   // Min distance before we consider it a pull gesture (increased from 10)
const SCROLL_TOP_BUFFER = 5;       // Allow small scroll position tolerance (was 0)
const MIN_PULL_VELOCITY = 0.3;     // Minimum downward velocity to start pull (pixels/ms)
const DEBOUNCE_MS = 100;           // Debounce time after scroll ends

/**
 * Global Pull-to-Refresh component for Capacitor mobile apps
 * Emits 'pull-to-refresh' event that components can listen to
 * Does NOT reload the page - components handle their own refresh logic
 */
export default function CapacitorPullToRefresh() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [canStartPull, setCanStartPull] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollY = useRef(0);
  const isMobile = Capacitor.isNativePlatform();

  // Handle refresh action - emit custom event for components to listen
  const handleRefresh = async () => {
    setIsRefreshing(true);
    
    try {
      // Emit custom event that components can listen to
      const event = new CustomEvent('pull-to-refresh', { 
        detail: { timestamp: Date.now() } 
      });
      window.dispatchEvent(event);
      
      // Wait a bit for components to refresh
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('[PullToRefresh] Refresh failed:', error);
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
      setIsPulling(false);
      setCanStartPull(false);
    }
  };

  // Track scroll position to detect when truly at top
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      lastScrollY.current = window.scrollY;
      
      // Reset pull state if user scrolled away from top
      if (window.scrollY > SCROLL_TOP_BUFFER) {
        setCanStartPull(false);
        setIsPulling(false);
        setPullDistance(0);
      }
      
      // Debounce: only allow pull after scroll has settled
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        if (window.scrollY <= SCROLL_TOP_BUFFER) {
          setCanStartPull(true);
        }
      }, DEBOUNCE_MS);
    };

    // Initial check
    if (window.scrollY <= SCROLL_TOP_BUFFER) {
      setCanStartPull(true);
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [isMobile]);

  // Pull-to-refresh touch handlers
  useEffect(() => {
    if (!isMobile) return;

    const handleTouchStart = (e: TouchEvent) => {
      // Only allow pull start if:
      // 1. We're at the top of the page (with buffer)
      // 2. canStartPull is true (scroll has settled)
      // 3. Not already refreshing
      if (window.scrollY <= SCROLL_TOP_BUFFER && canStartPull && !isRefreshing) {
        setTouchStart(e.touches[0].clientY);
        setTouchStartTime(Date.now());
        setIsPulling(true);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling || isRefreshing) return;

      const touchY = e.touches[0].clientY;
      const distance = touchY - touchStart;
      const timeDelta = Date.now() - touchStartTime;
      const velocity = timeDelta > 0 ? distance / timeDelta : 0;

      // Check if this looks like a genuine pull-down gesture:
      // - Must be pulling downward (positive distance)
      // - Must exceed start threshold
      // - Must have sufficient downward velocity OR be far enough
      const isGenuinePull = distance > PULL_START_THRESHOLD && 
        (velocity > MIN_PULL_VELOCITY || distance > PULL_THRESHOLD * 0.6);

      if (distance > 0 && isGenuinePull) {
        // Calculate rubber-band effect - slower the further you pull
        const rubberBandDistance = Math.min(
          PULL_START_THRESHOLD + (distance - PULL_START_THRESHOLD) * 0.5,
          150
        );
        setPullDistance(rubberBandDistance);

        // Prevent default scroll only when clearly pulling (not just scrolling up)
        if (distance > PULL_START_THRESHOLD * 1.5 && e.cancelable) {
          e.preventDefault();
        }
      } else if (distance <= 0) {
        // User is scrolling up, cancel pull
        setIsPulling(false);
        setPullDistance(0);
      }
    };

    const handleTouchEnd = () => {
      if (!isPulling) return;
      
      setIsPulling(false);

      // Trigger refresh if pulled far enough
      if (pullDistance > PULL_THRESHOLD && !isRefreshing) {
        handleRefresh();
      } else {
        setPullDistance(0);
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, isPulling, isRefreshing, pullDistance, touchStart, touchStartTime, canStartPull]);

  // Don't render if not mobile or not pulling
  if (!isMobile || pullDistance === 0) return null;

  const progress = Math.min(pullDistance / PULL_THRESHOLD, 1);

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[9999] flex justify-center transition-transform duration-200 pointer-events-none"
      style={{ 
        transform: `translateY(${Math.min(pullDistance - 40, 80)}px)`,
        opacity: progress
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-full shadow-lg p-3 border-2 border-blue-500">
        {isRefreshing || pullDistance > PULL_THRESHOLD ? (
          // Spinner when refreshing or pulled far enough
          <div className="relative w-6 h-6">
            <div className="absolute inset-0 border-2 border-blue-200 rounded-full"></div>
            <div className="absolute inset-0 border-2 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
          </div>
        ) : (
          // Arrow icon while pulling (rotates based on pull distance)
          <svg
            className="w-6 h-6 text-blue-500 transition-transform duration-100"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{ transform: `rotate(${progress * 180}deg)` }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        )}
      </div>
    </div>
  );
}
